import { api } from './index';

// Types for enhanced bidder management
export interface EnhancedBidder {
  id: string;
  tenderId: string;
  name: string;
  companyName: string;
  bidPrice: number;
  techScore?: number;
  finScore?: number;
  ranking?: number;
  isAwarded: boolean;
  representativeName?: string;
  representativeTitle?: string;
  representativePhone?: string;
  representativeEmail?: string;
  companyAddress?: string;
  registrationNumber?: string;
  taxId?: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'BLACKLISTED';
  qualificationStatus: 'PENDING' | 'QUALIFIED' | 'DISQUALIFIED';
  qualificationNotes?: string;
  hasTechnicalProposal: boolean;
  hasFinancialProposal: boolean;
  hasExperience: boolean;
  hasFinancialStatement: boolean;
  hasTaxClearance: boolean;
  hasInsurance: boolean;
  registeredById?: string;
  registeredAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface CreateBidderDto {
  companyName: string;
  representativeName?: string;
  representativeTitle?: string;
  representativePhone?: string;
  representativeEmail?: string;
  companyAddress?: string;
  registrationNumber?: string;
  taxId?: string;
  bidPrice: number;
  status?: 'ACTIVE' | 'SUSPENDED' | 'BLACKLISTED';
  qualificationStatus?: 'PENDING' | 'QUALIFIED' | 'DISQUALIFIED';
  qualificationNotes?: string;
}

export interface UpdateBidderDto extends Partial<CreateBidderDto> {}

export interface BidderSearchResult {
  id: string;
  companyName: string;
  representativeName?: string;
  representativeEmail?: string;
  registrationNumber?: string;
  tenderCount: number;
}

export interface BidderDocument {
  id: string;
  bidderId: string;
  documentType: 'TECHNICAL_PROPOSAL' | 'FINANCIAL_PROPOSAL' | 'EXPERIENCE' | 'FINANCIAL_STATEMENT' | 'TAX_CLEARANCE' | 'INSURANCE' | 'OTHER';
  fileName: string;
  fileSize: number;
  mimeType: string;
  bucket: string;
  objectKey: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewNotes?: string;
  uploadedById?: string;
  uploadedAt: string;
  reviewedById?: string;
  reviewedAt?: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface UploadBidderDocumentDto {
  documentType: 'TECHNICAL_PROPOSAL' | 'FINANCIAL_PROPOSAL' | 'EXPERIENCE' | 'FINANCIAL_STATEMENT' | 'TAX_CLEARANCE' | 'INSURANCE' | 'OTHER';
  fileName: string;
  fileSize: number;
  mimeType: string;
  bucket: string;
  objectKey: string;
}

export interface UpdateDocumentStatusDto {
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewNotes?: string;
}

// API functions
export const bidderApi = {
  // Register new bidder
  registerBidder: async (tenderId: string, data: CreateBidderDto): Promise<EnhancedBidder> => {
    const response = await api.post(`/tenders/${tenderId}/bidders`, data);
    return response.data;
  },

  // List bidders for a tender
  listBidders: async (tenderId: string): Promise<EnhancedBidder[]> => {
    const response = await api.get(`/tenders/${tenderId}/bidders`);
    return response.data;
  },

  // Update bidder
  updateBidder: async (tenderId: string, bidderId: string, data: UpdateBidderDto): Promise<EnhancedBidder> => {
    const response = await api.put(`/tenders/${tenderId}/bidders/${bidderId}`, data);
    return response.data;
  },

  // Remove bidder
  removeBidder: async (tenderId: string, bidderId: string): Promise<void> => {
    await api.delete(`/tenders/${tenderId}/bidders/${bidderId}`);
  },

  // Register existing bidder for new tender
  registerExistingBidder: async (tenderId: string, existingBidderId: string): Promise<EnhancedBidder> => {
    const response = await api.post(`/tenders/${tenderId}/bidders/from-existing`, { existingBidderId });
    return response.data;
  },

  // Search existing bidders
  searchExistingBidders: async (query: string, limit: number = 10): Promise<BidderSearchResult[]> => {
    const response = await api.get(`/tenders/bidders/search?query=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data;
  },

  // Get bidder details
  getBidderDetails: async (bidderId: string): Promise<EnhancedBidder> => {
    const response = await api.get(`/tenders/bidders/${bidderId}`);
    return response.data;
  },

  // Get bidder tender history
  getBidderTenderHistory: async (bidderId: string): Promise<any[]> => {
    const response = await api.get(`/tenders/bidders/${bidderId}/tenders`);
    return response.data;
  },

  // Upload bidder document
  uploadBidderDocument: async (tenderId: string, bidderId: string, data: UploadBidderDocumentDto): Promise<BidderDocument> => {
    const response = await api.post(`/tenders/${tenderId}/bidders/${bidderId}/documents`, data);
    return response.data;
  },

  // List bidder documents
  listBidderDocuments: async (tenderId: string, bidderId: string): Promise<BidderDocument[]> => {
    const response = await api.get(`/tenders/${tenderId}/bidders/${bidderId}/documents`);
    return response.data;
  },

  // Update document status
  updateDocumentStatus: async (tenderId: string, bidderId: string, docId: string, data: UpdateDocumentStatusDto): Promise<BidderDocument> => {
    const response = await api.put(`/tenders/${tenderId}/bidders/${bidderId}/documents/${docId}`, data);
    return response.data;
  },

  // Download bidder document
  downloadBidderDocument: async (tenderId: string, bidderId: string, docId: string): Promise<Blob> => {
    const response = await api.get(`/tenders/${tenderId}/bidders/${bidderId}/documents/${docId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Delete bidder document
  deleteBidderDocument: async (tenderId: string, bidderId: string, docId: string): Promise<void> => {
    await api.delete(`/tenders/${tenderId}/bidders/${bidderId}/documents/${docId}`);
  },
};
